import axios, { AxiosError, AxiosHeaders, InternalAxiosRequestConfig } from 'axios';
import { refreshToken } from '../services/auth';
import config from '../config/config';

// Extend AxiosRequestConfig to include retry flag
interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Event to notify all tabs/windows about logout
const logoutEvent = new Event('auth:logout');

const api = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      // Ensure headers exist and are the correct type
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const forceLogout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  window.dispatchEvent(logoutEvent);
  window.location.href = '/login';
};

const handleRefreshError = (error: any) => {
  
  processQueue(error, null);
  forceLogout();
  return Promise.reject(error);
};

// Response interceptor
api.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      
    }
    return response;
  },
  async (error: AxiosError & { config: RetryConfig }) => {
    const originalRequest = error.config as RetryConfig;
    if (!originalRequest) return Promise.reject(error);

    // Skip token refresh for auth endpoints and already retried requests
    const isAuthEndpoint = originalRequest.url?.includes('/auth/') ||
                          originalRequest.url?.includes('/register');
    
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        try {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              if (typeof token === 'string') {
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return api(originalRequest);
              }
              throw new Error('Invalid token received');
            });
        } catch (err) {
          return handleRefreshError(err);
        }
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const tokenResponse = await refreshToken();
        if (!tokenResponse) throw new Error('Failed to refresh token');
        
        const { accessToken } = tokenResponse;
        processQueue(null, accessToken);
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        return handleRefreshError(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
